<template>
  <div
    class="mx-auto max-w-[38rem] rounded-lg border border-static-default-low bg-surface-default-low p-24 md:p-32 shadow-short"
    data-test="other-sellers-feedback-card"
  >
    <div
      v-if="isSubmitted"
      class="py-32 text-center"
      data-test="feedback-success"
    >
      <h3 class="heading-2 text-static-default-hi">
        {{ i18n(translations.thankYouFeedback) }}
      </h3>
    </div>

    <div v-else class="flex flex-col gap-20">
      <!-- Card Header -->
      <div class="text-center">
        <h3 class="heading-2 text-static-default-hi">
          {{ i18n(translations.comingSoonTitle) }}
        </h3>

        <p class="mt-8 body-2 text-static-default-low">
          {{ i18n(translations.comingSoonDescription) }}
        </p>
      </div>

      <!-- Question -->
      <h4 class="text-center body-1-bold text-static-default-hi">
        {{ i18n(translations.feedbackQuestion) }}
      </h4>

      <!-- 2-Column Checkbox Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div class="flex flex-col gap-12">
          <RevCheckbox
            id="feedback-price"
            v-model="selectedReasons"
            :value="REASONS.PRICE"
            variant="full"
          >
            <template #label>{{ i18n(translations.feedbackPrice) }}</template>
          </RevCheckbox>

          <RevCheckbox
            id="feedback-seller"
            v-model="selectedReasons"
            :value="REASONS.SELLER"
            variant="full"
          >
            <template #label>{{ i18n(translations.feedbackSeller) }}</template>
          </RevCheckbox>

          <RevCheckbox
            id="feedback-not-use"
            v-model="selectedReasons"
            :value="REASONS.NOT_USE"
            variant="full"
          >
            <template #label>{{ i18n(translations.feedbackNotUse) }}</template>
          </RevCheckbox>
        </div>

        <div class="flex flex-col gap-12">
          <RevCheckbox
            id="feedback-seller-rating"
            v-model="selectedReasons"
            :value="REASONS.SELLER_RATING"
            variant="full"
          >
            <template #label>{{
              i18n(translations.feedbackSellerRating)
            }}</template>
          </RevCheckbox>

          <RevCheckbox
            id="feedback-delivery-speed"
            v-model="selectedReasons"
            :value="REASONS.DELIVERY_SPEED"
            variant="full"
          >
            <template #label>{{
              i18n(translations.feedbackDeliverySpeed)
            }}</template>
          </RevCheckbox>

          <RevCheckbox
            id="feedback-other"
            v-model="selectedReasons"
            :value="REASONS.OTHER"
            variant="full"
          >
            <template #label>{{ i18n(translations.feedbackOther) }}</template>
          </RevCheckbox>
        </div>
      </div>

      <!-- Textarea when 'Other' is checked -->
      <div
        v-if="isOtherSelected"
        class="mt-4"
        data-test="feedback-textarea-container"
      >
        <RevInputTextarea
          id="feedback-comment"
          v-model="comment"
          :maxlength="500"
          :label="i18n(translations.textareaLabel)"
          :placeholder="i18n(translations.textareaPlaceholder)"
          :rows="3"
        >
          <template #count="{ count }">{{ count }} / 500</template>
        </RevInputTextarea>
      </div>

      <!-- Validation Error -->
      <p
        v-if="errorMessage"
        class="caption text-static-danger-hi text-center"
        data-test="feedback-error"
      >
        {{ errorMessage }}
      </p>

      <!-- Submit CTA -->
      <RevButton
        class="w-full mt-4"
        size="medium"
        variant="primary"
        data-test="feedback-submit-cta"
        @click="handleSubmit"
      >
        {{ i18n(translations.sendAnswer) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevInputTextarea } from '@ds/components/InputTextarea'

import translations from '../../OtherSellersDrawer.translations'
import {
  OTHER_SELLERS_FEEDBACK_REASONS as REASONS,
  useOtherSellersTracking,
} from '../../composables/useOtherSellersTracking/useOtherSellersTracking'

const props = defineProps<{
  productId?: string | null
  offerId?: string | null
  merchantId?: string | null
}>()

const emit = defineEmits<{
  (e: 'submitted'): void
}>()

const i18n = useI18n()
const { trackOtherSellersFeedbackSubmitted } = useOtherSellersTracking()

const selectedReasons = ref<string[]>([])
const comment = ref('')
const errorMessage = ref<string | null>(null)
const isSubmitted = ref(false)

const isOtherSelected = computed(() =>
  selectedReasons.value.includes(REASONS.OTHER),
)

function handleSubmit() {
  errorMessage.value = null

  if (selectedReasons.value.length === 0) {
    errorMessage.value = i18n(translations.selectionRequiredError)
    return
  }

  if (isOtherSelected.value && !comment.value.trim()) {
    errorMessage.value = i18n(translations.commentRequiredError)
    return
  }

  trackOtherSellersFeedbackSubmitted({
    selectedReasons: selectedReasons.value,
    comment: comment.value.trim() || null,
    productId: props.productId,
    offerId: props.offerId,
    merchantId: props.merchantId,
  })

  isSubmitted.value = true
  emit('submitted')
}
</script>
